import React, { useState } from 'react'
import { Calendar, CalendarDays, Clock } from 'lucide-react'
import { format } from 'date-fns'
import { Button } from '@/components/ui/button'
import { Calendar as CalendarComponent } from '@/components/ui/calendar'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface TimeframeSelectorProps {
  timeFrame: string
  customDateRange?: {
    from: Date
    to: Date
  }
  onTimeFrameChange: (timeFrame: string) => void
  onCustomDateRangeChange: (dateRange: { from: Date; to: Date } | undefined) => void
}

const timeframeOptions = [
  { value: 'MTD', label: 'MTD' },
  { value: 'LAST_MONTH', label: 'Last month' },
  { value: 'LAST_QUARTER', label: 'Last Quarter' },
  { value: 'LAST_6_MONTHS', label: 'Last 6 months' },
  { value: 'LAST_1_YEAR', label: 'Last 1 year' },
  { value: 'CUSTOM', label: 'Custom' },
]

export const TimeframeSelector = ({ 
  timeFrame, 
  customDateRange, 
  onTimeFrameChange, 
  onCustomDateRangeChange 
}: TimeframeSelectorProps) => {
  const [tempDateRange, setTempDateRange] = useState<{from: Date | undefined, to: Date | undefined}>({
    from: customDateRange?.from,
    to: customDateRange?.to
  })
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)

  const handleTimeFrameChange = (value: string) => {
    onTimeFrameChange(value)
    if (value !== 'CUSTOM') {
      onCustomDateRangeChange(undefined)
    }
  }

  const handleDateRangeApply = () => {
    if (tempDateRange.from && tempDateRange.to) {
      onCustomDateRangeChange({
        from: tempDateRange.from,
        to: tempDateRange.to
      })
      setIsDatePickerOpen(false)
    }
  }

  const handleDateRangeCancel = () => {
    setTempDateRange({
      from: customDateRange?.from,
      to: customDateRange?.to
    })
    setIsDatePickerOpen(false)
  }

  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Timeframe:</span>
      </div>
      
      <Select value={timeFrame} onValueChange={handleTimeFrameChange}>
        <SelectTrigger className="w-48">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {timeframeOptions.map(option => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {timeFrame === 'CUSTOM' && (
        <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-[280px] justify-start text-left font-normal",
                !customDateRange && "text-muted-foreground"
              )}
            >
              <CalendarDays className="mr-2 h-4 w-4" />
              {customDateRange?.from ? (
                customDateRange.to ? (
                  <>
                    {format(customDateRange.from, "LLL dd, y")} -{" "}
                    {format(customDateRange.to, "LLL dd, y")}
                  </>
                ) : (
                  format(customDateRange.from, "LLL dd, y")
                )
              ) : (
                <span>Pick a date range</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 z-50" align="start">
            <div className="p-4 relative z-50">
              <CalendarComponent
                initialFocus
                mode="range"
                defaultMonth={tempDateRange?.from}
                selected={{
                  from: tempDateRange?.from,
                  to: tempDateRange?.to
                }}
                onSelect={(range) => {
                  setTempDateRange({
                    from: range?.from,
                    to: range?.to
                  })
                }}
                numberOfMonths={2}
                className={cn("p-3 pointer-events-auto relative z-50")}
              />
              <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDateRangeCancel}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleDateRangeApply}
                  disabled={!tempDateRange?.from || !tempDateRange?.to}
                >
                  Apply
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}